import React from 'react';
import type { ComponentType } from 'react';
import {
  Bell,
  Gift,
  Info,
  Link as LinkIcon,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import { SiInstagram, SiTiktok, SiX, SiYoutube } from 'react-icons/si';

import { Button } from '@components/ui/button';
import { cn } from '@components/lib/utils';

type IconCmp = ComponentType<{ className?: string }>;

/* Maps the legacy `IconType` string (block types from ICON_NAME_MAP +
 * capitalized social sources) to a glyph. Block types use lucide; brand
 * sources use Simple Icons (react-icons/si), matching the LinkInBio stack.
 * Unknown keys render no icon — same as the legacy iconMap fallback. */
const TAB_ICONS: Record<string, IconCmp> = {
  Link1: LinkIcon,
  Nft: Sparkles,
  Subscribe: Bell,
  Donations: Gift,
  Info,
  ShoppingBag,
  Instagram: SiInstagram as IconCmp,
  Tiktok: SiTiktok as IconCmp,
  Youtube: SiYoutube as IconCmp,
  Twitter: SiX as IconCmp,
};

interface ProfileRoundedTabProps {
  index: number;
  className?: string;
  onClick?: () => void;
  activeInd: number;
  IconType?: string;
  // Accepted (not used) so the legacy call sites keep type-checking; the
  // button is always an icon-only secondary tab.
  active?: boolean;
  buttonType?: string;
}

export const ProfileRoundedTab: React.FC<ProfileRoundedTabProps> = ({
  index,
  className,
  onClick,
  activeInd,
  IconType,
}) => {
  const isSelected = className?.includes('selected');
  const Glyph = IconType ? TAB_ICONS[IconType] : undefined;

  return (
    <li
      className={cn(
        'relative z-0 inline-flex items-center rounded-t-[20px] border-b-[1.5px] border-[#aaa]',
        !isSelected && 'px-[11px]',
        !isSelected && index === activeInd - 1 && 'pr-[3px]',
        !isSelected && index === activeInd + 1 && 'pl-[3px]',
        isSelected &&
          'z-[2] mx-2 mb-2 rounded-t-[20px] border-[1.5px] border-b-0 border-[#aaa] px-[10px] pt-[10px] pb-0 text-[#333] first:ml-0',
        // bottom corner notches that connect the selected tab to the underline
        isSelected &&
          "before:absolute before:bottom-[-8px] before:left-[-11px] before:h-[10px] before:w-[10px] before:rounded-br-[10px] before:border-[#aaa] before:border-r-[1.5px] before:border-b-[1.5px] before:content-[''] first:before:rounded-br-none",
        isSelected &&
          "after:absolute after:bottom-[-8px] after:right-[-11px] after:h-[10px] after:w-[10px] after:rounded-bl-[10px] after:border-[#aaa] after:border-b-[1.5px] after:border-l-[1.5px] after:content-['']",
        className,
      )}
    >
      <Button
        type="button"
        variant="secondary"
        size="icon"
        onClick={onClick}
        className="bg-marble-blue-100 text-marble-blue-600 hover:bg-marble-blue-200"
      >
        {Glyph ? <Glyph className="size-[22px]" /> : null}
      </Button>
    </li>
  );
};
